import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Send, CheckCircle2, Ban, Loader2, ArrowRight, ArrowRightLeft, AlertTriangle, Package, Store, Warehouse, Search, ShoppingBag, Wrench, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { catalogsAPI, getErrorMessage, inventoryAPI, settingsAPI, stockTransfersAPI, printAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { resolveCatalogCategoryPrefix } from '@/lib/productCatalog';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import { ResizableTableHandle } from '@/components/common/ResizableTableHandle';
import { useResizableTableColumns } from '@/hooks/useResizableTableColumns';
import { TABLE_DENSITY } from '@/lib/tableDensity';

const APPROVER_ROLES = ['admin', 'store_manager', 'supervisor', 'manager'];

const LEGACY_CATEGORY_LABEL_TO_PREFIX = {
  accessorio: 'AM',
  acessorio: 'AM',
  acessórios: 'AM',
  acessorios: 'AM',
  smartphone: 'SM',
  smartphones: 'SM',
  tablet: 'TB',
  tablets: 'TB',
  laptop: 'LP',
  laptops: 'LP',
  desktop: 'DS',
  desktops: 'DS',
  monitor: 'MN',
  monitores: 'MN',
  tv: 'MN',
  'all in one': 'AIO',
  'all-in-one': 'AIO',
  servidor: 'SW',
  servidores: 'SW',
  console: 'CN',
  consola: 'CN',
  consolas: 'CN',
  mba: 'SM',
  carregador: 'CHG',
  carregadores: 'CHG',
  peça: 'PC',
  peças: 'PC',
  peca: 'PC',
  pecas: 'PC',
  diversos: 'DV',
};

const statusClassName = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-300',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800 border-amber-300',
  APPROVED: 'bg-blue-100 text-blue-700 border-blue-300',
  COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  CANCELLED: 'bg-rose-100 text-rose-700 border-rose-300',
};

const INVENTORY_TABLE_HEAD_CLASS = TABLE_DENSITY.head;
const INVENTORY_TABLE_CELL_CLASS = `${TABLE_DENSITY.cell} text-[11px]`;
const INVENTORY_TABLE_ROW_CLASS = TABLE_DENSITY.row;
const TRANSFERS_SELECTOR_COLUMN_STYLE = { width: 34, minWidth: 34, maxWidth: 34 };
const STOCK_TRANSFERS_TABLE_COLUMN_WIDTHS = {
  number: 116,
  origin: 118,
  destination: 118,
  items: 82,
  status: 94,
  created: 118,
  approved: 108,
  actions: 214,
};

const emptyItem = () => ({ id: crypto.randomUUID(), product_id: '', qty: 1, category_filter: 'all', subcategory_filter: 'all' });

const slugifyLocationName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export default function StockTransfersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState('');

  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'ALL');
  const [tableSearch, setTableSearch] = useState(searchParams.get('q') || '');
  const [locations, setLocations] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [destinationProducts, setDestinationProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [productSubcategories, setProductSubcategories] = useState([]);
  const [inventoryProductsIndex, setInventoryProductsIndex] = useState({});
  const [transfers, setTransfers] = useState([]);
  const [selectedTransferIds, setSelectedTransferIds] = useState([]);
  const [availabilitySnapshots, setAvailabilitySnapshots] = useState({});

  const [originLocationId, setOriginLocationId] = useState(searchParams.get('origin_location_id') || '');
  const [destinationLocationId, setDestinationLocationId] = useState(searchParams.get('destination_location_id') || '');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [editingTransferId, setEditingTransferId] = useState('');
  const [editingTransferNumber, setEditingTransferNumber] = useState('');

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [transferToCancel, setTransferToCancel] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transferToDelete, setTransferToDelete] = useState(null);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const originManuallyChangedRef = useRef(false);
  const transfersSyncSnapshotRef = useRef('');
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();

  const canApprove = APPROVER_ROLES.includes(user?.role);
  const canDeleteTransfers = user?.role === 'admin';
  const { getColumnStyle: getTransferColumnStyle, startColumnResize: startTransferColumnResize } = useResizableTableColumns({
    storageKey: 'cg-stock-transfers-list-columns',
    defaultWidths: STOCK_TRANSFERS_TABLE_COLUMN_WIDTHS,
    minWidth: 72,
  });
  const requestedProductId = searchParams.get('product_id');
  const requestedSku = searchParams.get('sku');
  const requestedProductName = searchParams.get('product_name');
  const requestedOriginLocationId = searchParams.get('origin_location_id');
  const requestedOriginHandle = searchParams.get('origin');
  const requestedDestinationHandle = searchParams.get('destination');

  const locationHandleMaps = useMemo(() => {
    const idToHandle = {};
    const handleToId = {};

    locations.forEach((location) => {
      const baseHandle = slugifyLocationName(location.name) || `loc-${String(location.id || '').slice(0, 6)}`;
      let nextHandle = baseHandle;
      let suffix = 2;

      while (handleToId[nextHandle] && handleToId[nextHandle] !== location.id) {
        nextHandle = `${baseHandle}-${suffix}`;
        suffix += 1;
      }

      idToHandle[location.id] = nextHandle;
      handleToId[nextHandle] = location.id;
    });

    return { idToHandle, handleToId };
  }, [locations]);

  useEffect(() => {
    const nextStatus = searchParams.get('status') || 'ALL';
    const nextQuery = searchParams.get('q') || '';
    const nextOrigin = searchParams.get('origin_location_id') || '';
    const nextDestination = searchParams.get('destination_location_id') || '';
    const snapshot = `${nextStatus}|${nextQuery}|${nextOrigin}|${nextDestination}`;

    if (transfersSyncSnapshotRef.current === snapshot) return;
    transfersSyncSnapshotRef.current = snapshot;

    setStatusFilter((prev) => (prev === nextStatus ? prev : nextStatus));
    setTableSearch((prev) => (prev === nextQuery ? prev : nextQuery));
    if (nextOrigin && !originLocationId && !originManuallyChangedRef.current) {
      setOriginLocationId((prev) => (prev === nextOrigin ? prev : nextOrigin));
    }
    if (nextDestination && !destinationLocationId) {
      setDestinationLocationId((prev) => (prev === nextDestination ? prev : nextDestination));
    }
  }, [searchParams, originLocationId, destinationLocationId]);

  useEffect(() => {
    if (!locations.length) return;

    const nextOriginId = requestedOriginHandle ? locationHandleMaps.handleToId[requestedOriginHandle] || '' : '';
    const nextDestinationId = requestedDestinationHandle ? locationHandleMaps.handleToId[requestedDestinationHandle] || '' : '';

    if (nextOriginId && !originLocationId && !originManuallyChangedRef.current) {
      setOriginLocationId((prev) => (prev === nextOriginId ? prev : nextOriginId));
    }

    if (nextDestinationId && !destinationLocationId) {
      setDestinationLocationId((prev) => (prev === nextDestinationId ? prev : nextDestinationId));
    }
  }, [destinationLocationId, locationHandleMaps.handleToId, locations.length, originLocationId, requestedDestinationHandle, requestedOriginHandle]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'ALL') params.set('status', statusFilter);
    if (tableSearch.trim()) params.set('q', tableSearch.trim());
    if (originLocationId) {
      const originHandle = locationHandleMaps.idToHandle[originLocationId];
      if (originHandle) params.set('origin', originHandle);
      else params.set('origin_location_id', originLocationId);
    }
    if (destinationLocationId) {
      const destinationHandle = locationHandleMaps.idToHandle[destinationLocationId];
      if (destinationHandle) params.set('destination', destinationHandle);
      else params.set('destination_location_id', destinationLocationId);
    }
    if (requestedProductId) params.set('product_id', requestedProductId);
    if (requestedSku) params.set('sku', requestedSku);
    if (requestedProductName) params.set('product_name', requestedProductName);

    transfersSyncSnapshotRef.current = `${statusFilter}|${tableSearch.trim()}|${originLocationId}|${destinationLocationId}`;
    setSearchParams(params, { replace: true });
  }, [statusFilter, tableSearch, originLocationId, destinationLocationId, requestedProductId, requestedSku, requestedProductName, setSearchParams, locationHandleMaps.idToHandle]);

  const loadTransfers = useCallback(async () => {
    const response = await stockTransfersAPI.list({ status: statusFilter });
    setTransfers(response.data?.items || []);
  }, [statusFilter]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      resetFetchTelemetry();
      const [locationsRes, transfersRes, catalogsRes, subcategoriesRes] = await Promise.all([
        safeRequest(() => settingsAPI.listActiveLocations(), { data: [] }, { telemetry: { module: 'stock-transfers', endpoint: 'settings.activeLocations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => stockTransfersAPI.list({ status: statusFilter }), { data: { items: [] } }, { telemetry: { module: 'stock-transfers', endpoint: 'stockTransfers.list', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listProductCategories(), { data: [] }, { telemetry: { module: 'stock-transfers', endpoint: 'catalogs.productCategories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listProductSubcategories(), { data: [] }, { telemetry: { module: 'stock-transfers', endpoint: 'catalogs.productSubcategories', onEvent: captureFetchTelemetry } }),
      ]);

      const activeLocations = (locationsRes.data || []).filter(
        (loc) => loc.is_active !== false && loc.is_hidden !== true
      );

      setLocations(activeLocations);
      setTransfers(transfersRes.data?.items || []);
      setProductCategories(catalogsRes?.data || []);
      setProductSubcategories(subcategoriesRes?.data || []);

      if (activeLocations.length > 0 && !originLocationId) {
        setOriginLocationId(activeLocations[0].id);
      }
      if (activeLocations.length > 1 && !destinationLocationId) {
        setDestinationLocationId(activeLocations[1].id);
      }
    } catch (error) {
      toast.error('Falha ao carregar dados de transferências');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, originLocationId, destinationLocationId, captureFetchTelemetry, resetFetchTelemetry]);

  const loadInventoryProductsIndex = useCallback(async () => {
    try {
      const inventoryProductsRes = await safeRequest(
        () => inventoryAPI.listProducts(),
        { data: [] },
        { telemetry: { module: 'stock-transfers', endpoint: 'inventory.listProducts.deferred', onEvent: captureFetchTelemetry } },
      );
      setInventoryProductsIndex(
        Object.fromEntries(((inventoryProductsRes?.data || [])).map((product) => [product.id, product]))
      );
    } catch (error) {
      console.error('Falha ao carregar índice de produtos das transferências:', error);
    }
  }, [captureFetchTelemetry]);

  const loadOriginAvailability = useCallback(async () => {
    if (!originLocationId) {
      setAvailableProducts([]);
      return;
    }
    try {
      const response = await inventoryAPI.listPosAvailability({
        location_id: originLocationId,
        limit: 500,
      });
      setAvailableProducts((response.data?.products || []).filter((product) => Number(product.selected_location_available || 0) > 0));
    } catch (error) {
      toast.error('Não foi possível carregar disponibilidade da origem');
      setAvailableProducts([]);
    }
  }, [originLocationId]);

  const loadDestinationAvailability = useCallback(async () => {
    if (!destinationLocationId) {
      setDestinationProducts([]);
      return;
    }

    try {
      const response = await inventoryAPI.listPosAvailability({
        location_id: destinationLocationId,
        limit: 500,
      });
      setDestinationProducts(response.data?.products || []);
    } catch (error) {
      toast.error('Não foi possível carregar disponibilidade do destino');
      setDestinationProducts([]);
    }
  }, [destinationLocationId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!loading && Object.keys(inventoryProductsIndex).length === 0) {
      loadInventoryProductsIndex();
    }
  }, [inventoryProductsIndex, loading, loadInventoryProductsIndex]);

  useEffect(() => {
    loadOriginAvailability();
  }, [loadOriginAvailability]);

  useEffect(() => {
    loadDestinationAvailability();
  }, [loadDestinationAvailability]);

  useEffect(() => {
    if (!originLocationId || !destinationLocationId) return;
    if (originLocationId !== destinationLocationId) return;

    const fallbackDestination = locations.find((loc) => loc.id !== originLocationId)?.id || '';
    setDestinationLocationId(fallbackDestination);
  }, [originLocationId, destinationLocationId, locations]);

  useEffect(() => {
    const selectedProductIds = [...new Set(items.map((row) => row.product_id).filter(Boolean))];

    if (!originLocationId || selectedProductIds.length === 0) {
      setAvailabilitySnapshots({});
      return;
    }

    let active = true;

    const loadSnapshots = async () => {
      try {
        const responses = await Promise.all(
          selectedProductIds.map((productId) => stockTransfersAPI.availability(productId, originLocationId, destinationLocationId || null))
        );

        if (!active) return;

        const nextSnapshots = {};
        responses.forEach((response) => {
          const data = response.data;
          if (data?.product_id) {
            nextSnapshots[data.product_id] = data;
          }
        });
        setAvailabilitySnapshots(nextSnapshots);
      } catch (error) {
        if (!active) return;
        toast.error(getErrorMessage(error, 'Não foi possível validar disponibilidade real por localização'));
      }
    };

    loadSnapshots();

    return () => {
      active = false;
    };
  }, [destinationLocationId, items, originLocationId]);

  useEffect(() => {
    setPrefillApplied(false);
  }, [requestedProductId]);

  useEffect(() => {
    if (!requestedOriginLocationId || locations.length === 0 || originLocationId || originManuallyChangedRef.current) return;
    const exists = locations.some((location) => location.id === requestedOriginLocationId);
    if (exists) {
      setOriginLocationId(requestedOriginLocationId);
    }
  }, [locations, originLocationId, requestedOriginLocationId]);

  useEffect(() => {
    loadTransfers();
  }, [loadTransfers]);

  const productMap = useMemo(() => {
    const map = new Map();
    availableProducts.forEach((product) => map.set(product.id, product));
    return map;
  }, [availableProducts]);

  const destinationProductMap = useMemo(() => {
    const map = new Map();
    destinationProducts.forEach((product) => map.set(product.id, product));
    return map;
  }, [destinationProducts]);

  const findCatalogCategory = useCallback((product) => {
    if (!product) return null;

    const normalizedLegacyCategory = String(product.category || '').trim().toLowerCase();
    const resolvedLegacyPrefix = LEGACY_CATEGORY_LABEL_TO_PREFIX[normalizedLegacyCategory];

    const candidates = [
      product.category_id,
      product.category_name,
      product.category,
      resolveCatalogCategoryPrefix(product.category, product.sku),
      resolvedLegacyPrefix,
    ].filter(Boolean);

    for (const candidate of candidates) {
      const normalizedCandidate = String(candidate).trim().toLowerCase();
      const match = productCategories.find((category) => (
        category.id === candidate
        || String(category.base_prefix || '').trim().toLowerCase() === normalizedCandidate
        || String(category.name || '').trim().toLowerCase() === normalizedCandidate
      ));
      if (match) return match;
    }

    return null;
  }, [productCategories]);

  const findCatalogSubcategory = useCallback((product, category = null) => {
    if (!product) return null;

    const candidates = [product.subcategory_id, product.subcategory_name, product.subcategory].filter(Boolean);

    for (const candidate of candidates) {
      const normalizedCandidate = String(candidate).trim().toLowerCase();
      const match = productSubcategories.find((subcategory) => {
        if (category?.id && subcategory.category_id !== category.id) return false;
        return (
          subcategory.id === candidate
          || String(subcategory.prefix || '').trim().toLowerCase() === normalizedCandidate
          || String(subcategory.name || '').trim().toLowerCase() === normalizedCandidate
        );
      });
      if (match) return match;
    }

    return null;
  }, [productSubcategories]);

  const getProductDisplayData = useCallback((product) => {
    const inventoryProduct = inventoryProductsIndex[product?.id] || {};
    const mergedProduct = { ...inventoryProduct, ...product };
    const category = findCatalogCategory(mergedProduct);
    const subcategory = findCatalogSubcategory(mergedProduct, category);
    return {
      category,
      subcategory,
      categoryLabel: category?.name || mergedProduct?.category_name || mergedProduct?.category || 'Sem categoria',
      subcategoryLabel: subcategory?.name || mergedProduct?.subcategory_name || mergedProduct?.subcategory || '',
      categoryKey: category?.id || category?.base_prefix || mergedProduct?.category_id || resolveCatalogCategoryPrefix(mergedProduct?.category, mergedProduct?.sku) || mergedProduct?.category || 'uncategorized',
      subcategoryKey: subcategory?.id || mergedProduct?.subcategory_id || mergedProduct?.subcategory || 'all',
      productLabel: [mergedProduct?.sku, mergedProduct?.name].filter(Boolean).join(' · '),
    };
  }, [findCatalogCategory, findCatalogSubcategory, inventoryProductsIndex]);

  const requestedQtyByProduct = useMemo(() => {
    const totals = new Map();
    items.forEach((row) => {
      if (!row.product_id || Number(row.qty) <= 0) return;
      totals.set(row.product_id, (totals.get(row.product_id) || 0) + Number(row.qty));
    });
    return totals;
  }, [items]);

  const originLocationName = useMemo(
    () => locations.find((location) => location.id === originLocationId)?.name || 'Origem não definida',
    [locations, originLocationId],
  );

  const destinationLocationName = useMemo(
    () => locations.find((location) => location.id === destinationLocationId)?.name || 'Destino não definido',
    [locations, destinationLocationId],
  );

  const getRowStockSnapshot = useCallback((row) => {
    const selectedProduct = productMap.get(row.product_id);
    const destinationProduct = destinationProductMap.get(row.product_id);
    const availabilitySnapshot = availabilitySnapshots[row.product_id] || null;
    const snapshotLocations = availabilitySnapshot?.locations || {};
    const availableAtOrigin = Number(snapshotLocations?.[originLocationId]?.available ?? selectedProduct?.selected_location_available ?? 0);
    const availableAtDestination = Number(snapshotLocations?.[destinationLocationId]?.available ?? destinationProduct?.selected_location_available ?? 0);
    const requestedTotal = Number(requestedQtyByProduct.get(row.product_id) || 0);
    const remainingAfter = availableAtOrigin - requestedTotal;

    return {
      selectedProduct,
      destinationProduct,
      availabilitySnapshot,
      availableAtOrigin,
      availableAtDestination,
      requestedTotal,
      remainingAfter,
      isOverRequested: requestedTotal > availableAtOrigin,
    };
  }, [availabilitySnapshots, destinationLocationId, destinationProductMap, originLocationId, productMap, requestedQtyByProduct]);

  const filteredTransfers = useMemo(() => {
    if (!tableSearch.trim()) return transfers;
    const term = tableSearch.toLowerCase();
    return transfers.filter((transfer) => [
      transfer.transfer_number,
      transfer.origin_location_name,
      transfer.destination_location_name,
      transfer.status,
      transfer.approved_by_name,
    ].some((field) => String(field || '').toLowerCase().includes(term)));
  }, [transfers, tableSearch]);

  useEffect(() => {
    setSelectedTransferIds([]);
  }, [statusFilter, tableSearch]);

  const allTransfersSelected = filteredTransfers.length > 0 && filteredTransfers.every((transfer) => selectedTransferIds.includes(transfer.id));

  const toggleTransferSelection = (transferId, checked) => {
    setSelectedTransferIds((prev) => {
      if (checked) {
        return prev.includes(transferId) ? prev : [...prev, transferId];
      }
      return prev.filter((id) => id !== transferId);
    });
  };

  const toggleSelectAllTransfers = (checked) => {
    if (checked) {
      setSelectedTransferIds(filteredTransfers.map((transfer) => transfer.id));
      return;
    }
    setSelectedTransferIds([]);
  };

  const handleBulkTransferStatus = async (action) => {
    if (!selectedTransferIds.length) {
      toast.error('Seleccione transferências para ação em massa');
      return;
    }
    try {
      const response = await stockTransfersAPI.bulkStatus({
        ids: selectedTransferIds,
        action,
        reason: action === 'deactivate' ? 'Cancelamento em massa via lista' : 'Restauro em massa via lista',
      });
      toast.success(response.data.message || 'Ação em massa concluída');
      setSelectedTransferIds([]);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa'));
    }
  };

  const handleExportSelectedTransfers = () => {
    if (!selectedTransferIds.length) {
      toast.error('Seleccione transferências para exportar');
      return;
    }
    const selected = filteredTransfers.filter((transfer) => selectedTransferIds.includes(transfer.id));
    if (!selected.length) {
      toast.error('Nenhuma transferência seleccionada para exportação');
      return;
    }

    const headers = ['Número', 'Origem', 'Destino', 'Qtd. Itens', 'Estado', 'Criado em', 'Aprovado por'];
    const rows = selected.map((transfer) => {
      const totalQty = (transfer.items || []).reduce((sum, item) => sum + Number(item.qty || 0), 0);
      return [
        transfer.transfer_number || '',
        transfer.origin_location_name || '',
        transfer.destination_location_name || '',
        totalQty,
        transfer.status || '',
        formatDateTime(transfer.created_at),
        transfer.approved_by_name || '',
      ];
    });

    const csv = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `transferencias_seleccionadas_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`${selected.length} transferências exportadas`);
  };

  const availableCategoryOptions = useMemo(() => {
    const map = new Map();
    availableProducts.forEach((product) => {
      const productDisplay = getProductDisplayData(product);
      const key = productDisplay.categoryKey;
      if (!key || map.has(key)) return;
      map.set(key, {
        value: key,
        label: productDisplay.categoryLabel,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt', { sensitivity: 'base' }));
  }, [availableProducts, getProductDisplayData]);

  const getAvailableSubcategoryOptions = useCallback((categoryFilter) => {
    if (!categoryFilter || categoryFilter === 'all') return [];
    const map = new Map();
    availableProducts.forEach((product) => {
        const productDisplay = getProductDisplayData(product);
        if (productDisplay.categoryKey !== categoryFilter) return;
        const key = productDisplay.subcategoryKey;
        if (!key || map.has(key)) return;
        map.set(key, {
          value: key,
          label: productDisplay.subcategoryLabel,
        });
      });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt', { sensitivity: 'base' }));
  }, [availableProducts, getProductDisplayData]);

  useEffect(() => {
    if (!requestedProductId || prefillApplied || availableProducts.length === 0) return;

    const selectedProduct = availableProducts.find((product) => product.id === requestedProductId);
    if (!selectedProduct) return;
    const display = getProductDisplayData(selectedProduct);

    setItems([{
      id: crypto.randomUUID(),
      product_id: requestedProductId,
      qty: 1,
      category_filter: display.categoryKey,
      subcategory_filter: display.subcategoryKey,
    }]);
    setNotes((prev) => prev || `Transferência iniciada a partir do inventário para ${requestedSku || selectedProduct.sku}${requestedProductName ? ` · ${requestedProductName}` : ''}`);
    setPrefillApplied(true);
    toast.success(`Produto ${requestedSku || selectedProduct.sku} preparado para transferência`);

    const nextParams = new URLSearchParams(searchParams);
    ['product_id', 'sku', 'product_name', 'origin_location_id'].forEach((key) => nextParams.delete(key));
    setSearchParams(nextParams, { replace: true });
  }, [availableProducts, getProductDisplayData, prefillApplied, requestedProductId, requestedProductName, requestedSku, searchParams, setSearchParams]);

  const resetForm = () => {
    setItems([emptyItem()]);
    setNotes('');
    setEditingTransferId('');
    setEditingTransferNumber('');
  };

  const handleOriginLocationChange = (nextOriginId) => {
    if (nextOriginId === originLocationId) return;

    originManuallyChangedRef.current = true;

    if (nextOriginId === destinationLocationId) {
      const fallbackDestination = locations.find((loc) => loc.id !== nextOriginId)?.id || '';
      setDestinationLocationId(fallbackDestination);
    }

    setOriginLocationId(nextOriginId);
    setItems((prev) => prev.map((row) => ({ ...row, product_id: '' })));
    setAvailabilitySnapshots({});
  };

  const loadTransferIntoForm = (transfer) => {
    const mappedItems = (transfer.items || []).map((item) => ({
      id: crypto.randomUUID(),
      product_id: item.product_id,
      qty: Number(item.qty || 1),
      category_filter: 'all',
      subcategory_filter: 'all',
    }));

    setOriginLocationId(transfer.origin_location_id || '');
    setDestinationLocationId(transfer.destination_location_id || '');
    setNotes(transfer.notes || '');
    setItems(mappedItems.length > 0 ? mappedItems : [emptyItem()]);
    setEditingTransferId(transfer.id);
    setEditingTransferNumber(transfer.transfer_number || '');
    toast.success(`Transferência ${transfer.transfer_number || ''} carregada para edição`);
  };

  const addItemRow = () => {
    setItems((prev) => [...prev, emptyItem()]);
  };

  const removeItemRow = (rowId) => {
    setItems((prev) => prev.filter((row) => row.id !== rowId));
  };

  const updateItemRow = (rowId, key, value) => {
    setItems((prev) => prev.map((row) => {
      if (row.id !== rowId) return row;

      if (key === 'category_filter') {
        return {
          ...row,
          category_filter: value,
          subcategory_filter: 'all',
          product_id: '',
        };
      }

      if (key === 'subcategory_filter') {
        return {
          ...row,
          subcategory_filter: value,
          product_id: '',
        };
      }

      if (key === 'product_id') {
        const selectedProduct = productMap.get(value);
        const display = getProductDisplayData(selectedProduct);
        return {
          ...row,
          product_id: value,
          category_filter: display?.categoryKey || row.category_filter,
          subcategory_filter: display?.subcategoryKey || row.subcategory_filter,
        };
      }

      return { ...row, [key]: value };
    }));
  };

  const createTransfer = async () => {
    if (!originLocationId || !destinationLocationId) {
      toast.error('Selecione origem e destino');
      return;
    }
    if (originLocationId === destinationLocationId) {
      toast.error('Origem e destino devem ser diferentes');
      return;
    }
    if (!notes.trim()) {
      toast.error('Motivo/observação é obrigatório');
      return;
    }

    const normalizedItems = items
      .filter((row) => row.product_id && Number(row.qty) > 0)
      .map((row) => ({ product_id: row.product_id, qty: Number(row.qty) }));

    if (normalizedItems.length === 0) {
      toast.error('Adicione ao menos 1 item válido');
      return;
    }

    const stockConflict = normalizedItems.find((item) => {
      const product = productMap.get(item.product_id);
      const snapshot = availabilitySnapshots[item.product_id];
      const requestedQty = requestedQtyByProduct.get(item.product_id) || 0;
      const originAvailable = Number(snapshot?.locations?.[originLocationId]?.available ?? product?.selected_location_available ?? 0);
      return requestedQty > originAvailable;
    });

    if (stockConflict) {
      const product = productMap.get(stockConflict.product_id);
      toast.error(`Quantidade excede o stock disponível na origem para ${product?.sku || 'o artigo seleccionado'}`);
      return;
    }

    setCreating(true);
    try {
      if (editingTransferId) {
        await stockTransfersAPI.update(editingTransferId, {
          origin_location_id: originLocationId,
          destination_location_id: destinationLocationId,
          items: normalizedItems,
          notes,
        });
        toast.success('Transferência DRAFT actualizada com sucesso');
      } else {
        await stockTransfersAPI.create({
          origin_location_id: originLocationId,
          destination_location_id: destinationLocationId,
          items: normalizedItems,
          notes,
        });
        toast.success('Transferência criada em DRAFT');
      }
      resetForm();
      await loadTransfers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao criar transferência');
    } finally {
      setCreating(false);
    }
  };

  const submitTransfer = async (transferId) => {
    setActionLoadingId(`submit-${transferId}`);
    try {
      await stockTransfersAPI.submit(transferId);
      toast.success('Transferência enviada para aprovação');
      await loadTransfers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Falha ao submeter transferência');
    } finally {
      setActionLoadingId('');
    }
  };

  const approveTransfer = async (transferId) => {
    setActionLoadingId(`approve-${transferId}`);
    try {
      await stockTransfersAPI.approve(transferId);
      toast.success('Transferência aprovada e stock movimentado');
      await loadTransfers();
      await loadOriginAvailability();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Falha ao aprovar transferência');
    } finally {
      setActionLoadingId('');
    }
  };

  const requestCancelTransfer = (transfer) => {
    setTransferToCancel(transfer);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const requestDeleteTransfer = (transfer) => {
    if (!canDeleteTransfers) return;
    setTransferToDelete(transfer);
    setDeleteDialogOpen(true);
  };

  const confirmCancelTransfer = async () => {
    if (!transferToCancel) return;
    if (!cancelReason.trim()) {
      toast.error('Motivo de cancelamento é obrigatório');
      return;
    }
    setActionLoadingId(`cancel-${transferToCancel.id}`);
    try {
      await stockTransfersAPI.cancel(transferToCancel.id, cancelReason);
      toast.success('Transferência cancelada');
      setCancelDialogOpen(false);
      setTransferToCancel(null);
      setCancelReason('');
      await loadTransfers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Falha ao cancelar transferência');
    } finally {
      setActionLoadingId('');
    }
  };

  const confirmDeleteTransfer = async () => {
    if (!transferToDelete?.id) return;
    setActionLoadingId(`delete-${transferToDelete.id}`);
    try {
      await stockTransfersAPI.delete(transferToDelete.id);
      toast.success('Transferência apagada');
      setDeleteDialogOpen(false);
      setTransferToDelete(null);
      setSelectedTransferIds((prev) => prev.filter((id) => id !== transferToDelete.id));
      await loadTransfers();
      await loadOriginAvailability();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Falha ao apagar transferência'));
    } finally {
      setActionLoadingId('');
    }
  };

  const openTransferPdf = async (transfer, copyType = 'Original') => {
    if (!transfer?.id) return;
    try {
      await printAPI.renderDocumentPdf({
        doc_type: 'stock_transfer',
        transfer_id: transfer.id,
        module_group: 'STOCK',
        copy_type: copyType
      }, 'preview');
    } catch (error) {
      toast.error(error?.message || 'Erro ao gerar PDF da transferência');
    }
  };

  const hasStockTransferData = Boolean(transfers.length || locations.length || productCategories.length || Object.keys(inventoryProductsIndex).length);

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasStockTransferData}
      partialIssues={partialIssues}
      loadingTestId="stock-transfers-loading-state"
      partialIssuesTestId="stock-transfers-partial-issues"
      emptyState={
        <div className="space-y-6 bg-slate-50/70 p-4 md:p-6 lg:p-8" data-testid="stock-transfers-page">
          <EmptyStatePanel
            title="Sem dados disponíveis"
            description="Nenhum registo encontrado para Transferências de Stock. Pode configurar ou importar dados para começar."
            testId="stock-transfers-empty-state"
          />
        </div>
      }
    >
    <div className="space-y-4 bg-slate-50/70 p-4 md:p-6 lg:p-8" data-testid="stock-transfers-page">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="stock-transfers-page-title">Transferências de Stock</h1>
          <p className="text-xs text-slate-500" data-testid="stock-transfers-page-subtitle">
            Vista compacta com rota, disponibilidade e aprovação.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="stock-transfers-context-controls-row">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/procurement')}
            data-testid="stock-transfers-open-procurement-button"
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            Compras
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/services')}
            data-testid="stock-transfers-open-services-button"
          >
            <Wrench className="h-4 w-4 mr-2" />
            Serviços
          </Button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={tableSearch}
              onChange={(event) => setTableSearch(event.target.value)}
              placeholder="Pesquisar transferência, origem, destino"
              className="h-10 w-64 rounded-xl border-slate-300 bg-white pl-9"
              data-testid="stock-transfers-context-search-input"
            />
          </div>
          <Badge variant="outline" className="rounded-full border-slate-300 bg-slate-100 px-3 py-1 text-slate-700">
            {availableProducts.length} artigos disponíveis na origem
          </Badge>
          <Badge variant="outline" className="rounded-full border-indigo-200 bg-indigo-50 px-3 py-1 text-indigo-700">
            {filteredTransfers.length} transferências listadas
          </Badge>
        </div>
      </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr] xl:items-start">
      <Card className="rounded-2xl border-slate-200 bg-white shadow-sm" data-testid="stock-transfer-create-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            {editingTransferId ? `Editar Transferência ${editingTransferNumber || ''}` : 'Nova Transferência'}
          </CardTitle>
          <CardDescription className="text-xs">Origem, destino, itens e motivo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 pt-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="stock-transfer-route-card">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <ArrowRightLeft className="h-4 w-4 text-indigo-600" />
              Rota da transferência
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto_1fr] lg:items-end">
            <div className="space-y-2">
              <Label>Origem</Label>
              <Select value={originLocationId} onValueChange={handleOriginLocationChange}>
                <SelectTrigger className="h-11 rounded-xl border-slate-300 bg-white" data-testid="transfer-origin-select">
                  <SelectValue placeholder="Selecionar origem" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id} data-testid={`transfer-origin-option-${loc.id}`}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-center pb-2 text-slate-400 lg:pb-3">
              <ArrowRight className="h-5 w-5" />
            </div>

            <div className="space-y-2">
              <Label>Destino</Label>
              <Select value={destinationLocationId} onValueChange={setDestinationLocationId}>
                <SelectTrigger className="h-11 rounded-xl border-slate-300 bg-white" data-testid="transfer-destination-select">
                  <SelectValue placeholder="Selecionar destino" />
                </SelectTrigger>
                <SelectContent>
                  {locations
                    .filter((loc) => loc.id !== originLocationId)
                    .map((loc) => (
                      <SelectItem key={loc.id} value={loc.id} data-testid={`transfer-destination-option-${loc.id}`}>
                        {loc.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Badge variant="outline" data-testid="stock-transfer-origin-summary"><Warehouse className="mr-1 h-3.5 w-3.5 text-indigo-600" />{originLocationName}</Badge>
              <Badge variant="outline" data-testid="stock-transfer-destination-summary"><Store className="mr-1 h-3.5 w-3.5 text-emerald-600" />{destinationLocationName}</Badge>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Motivo / Observação</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Reposição da loja devido ruptura de stock"
              data-testid="transfer-notes-input"
            />
          </div>

          <div className="space-y-3" data-testid="transfer-items-section">
            {items.map((row, idx) => {
              const { selectedProduct, availableAtOrigin, availableAtDestination, requestedTotal, remainingAfter, isOverRequested } = getRowStockSnapshot(row);
              const subcategoryOptions = getAvailableSubcategoryOptions(row.category_filter);
              const filteredProductOptions = availableProducts.filter((product) => {
                const productDisplay = getProductDisplayData(product);
                const categoryMatches = row.category_filter === 'all' || productDisplay.categoryKey === row.category_filter;
                const subcategoryMatches = row.subcategory_filter === 'all' || productDisplay.subcategoryKey === row.subcategory_filter;
                return categoryMatches && subcategoryMatches;
              });
              const selectedDisplay = getProductDisplayData(selectedProduct);

              return (
                <div key={row.id} className={cn(
                  "grid grid-cols-1 gap-3 rounded-2xl border p-3",
                  isOverRequested ? 'border-red-300 bg-red-50/50' : 'border-slate-200 bg-white'
                )} data-testid={`transfer-item-card-${idx}`}>
                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-[220px_240px] xl:items-end">
                  <div className="space-y-1">
                    <Label>Categoria</Label>
                    <Select value={row.category_filter || 'all'} onValueChange={(value) => updateItemRow(row.id, 'category_filter', value)}>
                      <SelectTrigger className="h-11 rounded-xl border-slate-300 bg-slate-50" data-testid={`transfer-item-category-select-${idx}`}>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas categorias</SelectItem>
                        {availableCategoryOptions.map((category) => (
                          <SelectItem key={category.value} value={category.value} data-testid={`transfer-item-category-option-${idx}-${category.value}`}>
                            {category.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label>Subcategoria</Label>
                    <Select value={row.subcategory_filter || 'all'} onValueChange={(value) => updateItemRow(row.id, 'subcategory_filter', value)}>
                      <SelectTrigger className="h-11 rounded-xl border-slate-300 bg-slate-50" data-testid={`transfer-item-subcategory-select-${idx}`}>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas subcategorias</SelectItem>
                        {subcategoryOptions.map((subcategory) => (
                          <SelectItem key={subcategory.value} value={subcategory.value} data-testid={`transfer-item-subcategory-option-${idx}-${subcategory.value}`}>
                            {subcategory.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_140px_120px] xl:items-end">
                  <div className="space-y-1">
                    <Label>Produto #{idx + 1}</Label>
                    <Select value={row.product_id} onValueChange={(value) => updateItemRow(row.id, 'product_id', value)}>
                      <SelectTrigger className="h-11 rounded-xl border-slate-300 bg-slate-50" data-testid={`transfer-item-product-select-${idx}`}>
                        <SelectValue placeholder="Selecionar produto" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredProductOptions.map((product) => {
                          const productDisplay = getProductDisplayData(product);
                          return (
                            <SelectItem key={product.id} value={product.id} data-testid={`transfer-item-product-option-${idx}-${product.id}`}>
                              {productDisplay.productLabel}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground" data-testid={`transfer-item-available-origin-${idx}`}>
                      {selectedProduct
                        ? `${selectedDisplay.categoryLabel}${selectedDisplay.subcategoryLabel ? ` › ${selectedDisplay.subcategoryLabel}` : ''}`
                        : `Filtrados: ${filteredProductOptions.length} artigos disponíveis`}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <Label>Quantidade</Label>
                    <Input
                      type="number"
                      min={1}
                      className="h-11 rounded-xl border-slate-300 bg-slate-50"
                      value={row.qty}
                      onChange={(e) => updateItemRow(row.id, 'qty', Number(e.target.value || 0))}
                      data-testid={`transfer-item-qty-input-${idx}`}
                    />
                  </div>

                  <div className="flex gap-2 lg:justify-end">
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 rounded-xl border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                        onClick={() => removeItemRow(row.id)}
                        data-testid={`transfer-item-remove-button-${idx}`}
                      >
                        Remover
                      </Button>
                    )}
                  </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline" data-testid={`transfer-item-origin-stock-card-${idx}`}>Origem: {availableAtOrigin}</Badge>
                    <Badge variant="outline" data-testid={`transfer-item-destination-stock-card-${idx}`}>Destino: {availableAtDestination}</Badge>
                    <Badge variant="outline" data-testid={`transfer-item-requested-stock-card-${idx}`}>Pedido: {requestedTotal}</Badge>
                    <div className={cn(
                      'rounded-full border px-3 py-1',
                      isOverRequested ? 'border-red-200 bg-red-100/70' : 'border-emerald-200 bg-emerald-50'
                    )} data-testid={`transfer-item-remaining-stock-card-${idx}`}>
                      <p className={cn('text-xs font-medium', isOverRequested ? 'text-red-700' : 'text-emerald-700')}>Saldo: {remainingAfter}</p>
                    </div>
                  </div>

                  {isOverRequested && (
                    <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-100/70 px-3 py-2 text-sm text-red-700" data-testid={`transfer-item-overstock-warning-${idx}`}>
                      <AlertTriangle className="h-4 w-4" />
                      A quantidade pedida excede o stock disponível na origem.
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="h-11 rounded-xl border-slate-300 bg-white text-slate-700 hover:bg-slate-50" onClick={addItemRow} data-testid="transfer-add-item-row-button">
              <Plus className="h-4 w-4 mr-1" />
              Adicionar Item
            </Button>
            <Button
              type="button"
              className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800"
              onClick={createTransfer}
              disabled={creating}
              data-testid={editingTransferId ? 'transfer-update-draft-button' : 'transfer-create-draft-button'}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              {editingTransferId ? 'Guardar alterações DRAFT' : 'Criar DRAFT'}
            </Button>
            {editingTransferId && (
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50"
                onClick={resetForm}
                data-testid="transfer-cancel-edit-button"
              >
                Cancelar edição
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200 bg-white shadow-sm" data-testid="stock-transfers-list-card">
        <CardHeader className="flex flex-col gap-3 border-b border-slate-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Transferências</CardTitle>
            <CardDescription className="text-xs">Lista operacional compacta.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 w-40 rounded-xl border-slate-300 bg-slate-50 shadow-sm" data-testid="stock-transfer-status-filter">
                <SelectValue placeholder="Filtrar estado" />
              </SelectTrigger>
              <SelectContent>
                {['ALL', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'COMPLETED', 'CANCELLED'].map((status) => (
                  <SelectItem key={status} value={status} data-testid={`stock-transfer-status-filter-option-${status}`}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTransferIds.length > 0 && (
              <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700" data-testid="stock-transfers-selection-badge">
                {selectedTransferIds.length} seleccionad{selectedTransferIds.length > 1 ? 'os' : 'o'}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={!selectedTransferIds.length}
              onClick={() => handleBulkTransferStatus('deactivate')}
              data-testid="stock-transfers-bulk-deactivate-button"
            >
              Cancelar ({selectedTransferIds.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!selectedTransferIds.length}
              onClick={() => handleBulkTransferStatus('restore_soft')}
              data-testid="stock-transfers-bulk-restore-button"
            >
              Restaurar DRAFT
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!selectedTransferIds.length}
              onClick={handleExportSelectedTransfers}
              data-testid="stock-transfers-bulk-export-button"
            >
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="overflow-x-auto" data-testid="stock-transfers-table-wrapper">
          <Table className="text-[11px] leading-tight" data-testid="stock-transfers-table">
            <TableHeader className="sticky top-0 z-10 bg-white">
              <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'sticky left-0 z-20 bg-slate-50/95')} style={TRANSFERS_SELECTOR_COLUMN_STYLE}>
                  <Checkbox
                    checked={allTransfersSelected}
                    onCheckedChange={(checked) => toggleSelectAllTransfers(Boolean(checked))}
                    data-testid="stock-transfers-select-all-checkbox"
                  />
                </TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getTransferColumnStyle('number')}>Nº Transfer<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('number', event)} testId="stock-transfers-column-resize-number" /></TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getTransferColumnStyle('origin')}>Origem<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('origin', event)} testId="stock-transfers-column-resize-origin" /></TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getTransferColumnStyle('destination')}>Destino<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('destination', event)} testId="stock-transfers-column-resize-destination" /></TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getTransferColumnStyle('items')}>Itens<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('items', event)} testId="stock-transfers-column-resize-items" /></TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getTransferColumnStyle('status')}>Estado<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('status', event)} testId="stock-transfers-column-resize-status" /></TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getTransferColumnStyle('created')}>Criado<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('created', event)} testId="stock-transfers-column-resize-created" /></TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getTransferColumnStyle('approved')}>Aprovado<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('approved', event)} testId="stock-transfers-column-resize-approved" /></TableHead>
                <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-right')} style={getTransferColumnStyle('actions')}>Ações<ResizableTableHandle onMouseDown={(event) => startTransferColumnResize('actions', event)} testId="stock-transfers-column-resize-actions" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTransfers.map((transfer) => {
                const totalQty = (transfer.items || []).reduce((sum, item) => sum + Number(item.qty || 0), 0);
                const isSelected = selectedTransferIds.includes(transfer.id);
                return (
                  <TableRow key={transfer.id} className={cn(INVENTORY_TABLE_ROW_CLASS, isSelected && 'bg-indigo-50/35')} data-testid={`stock-transfer-row-${transfer.id}`}>
                    <TableCell className={cn(INVENTORY_TABLE_CELL_CLASS, 'sticky left-0 z-10 bg-white group-hover:bg-indigo-50/35')} style={TRANSFERS_SELECTOR_COLUMN_STYLE}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => toggleTransferSelection(transfer.id, Boolean(checked))}
                        data-testid={`stock-transfer-select-checkbox-${transfer.id}`}
                      />
                    </TableCell>
                    <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`stock-transfer-number-${transfer.id}`} style={getTransferColumnStyle('number')}>
                      <div className="space-y-1">
                        <p className="font-medium text-slate-900">{transfer.transfer_number}</p>
                        <p className="text-[10px] text-slate-500">{formatDateTime(transfer.created_at)}</p>
                      </div>
                    </TableCell>
                    <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getTransferColumnStyle('origin')}>{transfer.origin_location_name}</TableCell>
                    <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getTransferColumnStyle('destination')}>{transfer.destination_location_name}</TableCell>
                    <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`stock-transfer-total-qty-${transfer.id}`} style={getTransferColumnStyle('items')}>
                      <p className="font-medium text-slate-900">{(transfer.items || []).length}</p>
                    </TableCell>
                    <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getTransferColumnStyle('status')}>
                      <Badge className={cn('border', statusClassName[transfer.status] || 'bg-slate-100')} data-testid={`stock-transfer-status-${transfer.id}`}>
                        {transfer.status}
                      </Badge>
                    </TableCell>
                    <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getTransferColumnStyle('created')}>{formatDateTime(transfer.created_at)}</TableCell>
                    <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`stock-transfer-approved-by-${transfer.id}`} style={getTransferColumnStyle('approved')}>{transfer.approved_by_name || '—'}</TableCell>
                    <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`} style={getTransferColumnStyle('actions')}>
                      <div className="flex flex-nowrap items-center justify-end gap-1 overflow-x-auto">
                        {transfer.status === 'DRAFT' && (
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7 rounded-md"
                            onClick={() => loadTransferIntoForm(transfer)}
                            title="Editar"
                            data-testid={`stock-transfer-edit-button-${transfer.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            <span className="sr-only">Editar</span>
                          </Button>
                        )}

                        {transfer.status === 'DRAFT' && (
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7 rounded-md border-blue-200 text-blue-700 hover:bg-blue-50"
                            onClick={() => submitTransfer(transfer.id)}
                            disabled={actionLoadingId === `submit-${transfer.id}`}
                            title="Submeter"
                            data-testid={`stock-transfer-submit-button-${transfer.id}`}
                          >
                            {actionLoadingId === `submit-${transfer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                            <span className="sr-only">Submeter</span>
                          </Button>
                        )}

                        {transfer.status === 'PENDING_APPROVAL' && canApprove && (
                          <Button
                            size="icon"
                            className="h-7 w-7 rounded-md bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => approveTransfer(transfer.id)}
                            disabled={actionLoadingId === `approve-${transfer.id}`}
                            title="Aprovar"
                            data-testid={`stock-transfer-approve-button-${transfer.id}`}
                          >
                            {actionLoadingId === `approve-${transfer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                            <span className="sr-only">Aprovar</span>
                          </Button>
                        )}

                        {['DRAFT', 'PENDING_APPROVAL'].includes(transfer.status) && (
                          <Button
                            size="icon"
                            variant="destructive"
                            className="h-7 w-7 rounded-md"
                            onClick={() => requestCancelTransfer(transfer)}
                            disabled={actionLoadingId === `cancel-${transfer.id}`}
                            title="Cancelar"
                            data-testid={`stock-transfer-cancel-button-${transfer.id}`}
                          >
                            <Ban className="h-3.5 w-3.5" />
                            <span className="sr-only">Cancelar</span>
                          </Button>
                        )}

                        {canDeleteTransfers && (
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7 rounded-md border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => requestDeleteTransfer(transfer)}
                            disabled={actionLoadingId === `delete-${transfer.id}`}
                            title="Apagar"
                            data-testid={`stock-transfer-delete-button-${transfer.id}`}
                          >
                            {actionLoadingId === `delete-${transfer.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            <span className="sr-only">Apagar</span>
                          </Button>
                        )}

                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="h-7 rounded-md px-2 text-[10px]"
                            onClick={() => openTransferPdf(transfer, 'Original')}
                            data-testid={`stock-transfer-pdf-original-button-${transfer.id}`}
                          >
                            Ori
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 rounded-md px-2 text-[10px]"
                            onClick={() => openTransferPdf(transfer, 'Duplicado')}
                            data-testid={`stock-transfer-pdf-duplicate-button-${transfer.id}`}
                          >
                            Dup
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
      </div>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent data-testid="stock-transfer-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar Transferência</DialogTitle>
            <DialogDescription>
              Informe o motivo obrigatório do cancelamento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="stock-transfer-cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} data-testid="stock-transfer-cancel-dialog-close-button">
              Fechar
            </Button>
            <Button variant="destructive" onClick={confirmCancelTransfer} data-testid="stock-transfer-cancel-dialog-confirm-button">
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="stock-transfer-delete-dialog">
          <DialogHeader>
            <DialogTitle>Apagar Transferência</DialogTitle>
            <DialogDescription>
              Esta acção remove permanentemente a transferência e os registos ligados. Apenas administradores podem executar esta operação.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="stock-transfer-delete-warning">
            {transferToDelete?.transfer_number || 'Transferência seleccionada'}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="stock-transfer-delete-dialog-close-button">
              Fechar
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTransfer} data-testid="stock-transfer-delete-dialog-confirm-button">
              Confirmar Apagar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ModuleStateBoundary>
  );
}
